import { pick } from 'lodash-es'
import { MeshBasicMaterial, Object3D, Vector3 } from 'three'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import type { Text } from 'troika-three-text'
import { disposeCadText, scheduleCadTextSync } from '@/plugins/cad/diagnostics/cadTextSync'
import {
  type AlignDimensionEntity,
  getAlignDimensionPoints,
} from '@/plugins/cad/module/CADModule/model/entity'
import { createCadLineSegments2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import type { LineTypeMaterial } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import { createCadText } from '@/plugins/cad/module/CADModule/object/text/CadTextFactory'
import { DrawUtils } from '@/plugins/cad/module/utils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { DEFAULT_UNIT_PREFERENCE, formatLength } from '@/shared/unitPresentation'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'
import { ArrowObject } from './ArrowObject'

/** 对齐标注渲染对象 */
export class DimensionObject extends Object3D {
  private lineObject: LineSegments2
  private leftArrowObject: ArrowObject
  private rightArrowObject: ArrowObject
  private textObject: Text
  /** 是否使用缓存材质，true 时销毁对象不释放材质 */
  public isCacheMaterial: boolean

  constructor(
    public entity: AlignDimensionEntity,
    material?: LineTypeMaterial,
    color?: number,
    private unitPresentation?: UnitPresentationService
  ) {
    super()
    this.isCacheMaterial = !!material
    const { p1, p2, offsetPoint } = this.entity
    const dimPointData = getAlignDimensionPoints(
      new Vector3(p1.x, p1.y, 0),
      new Vector3(p2.x, p2.y, 0),
      new Vector3(offsetPoint.x, offsetPoint.y, 0)
    )
    this.lineObject = this.createLineObject(dimPointData, material)
    this.lineObject.computeLineDistances()
    const { leftArrow, rightArrow } = this.createArrowObject(dimPointData, color)
    this.leftArrowObject = leftArrow
    this.rightArrowObject = rightArrow
    this.textObject = this.createTextObject(dimPointData, color)
    this.init()
  }

  private get materialColor() {
    return this.entity.color
  }

  /** 当前标注线段使用的线型材质 */
  get material() {
    return this.lineObject.material as LineTypeMaterial
  }

  private init() {
    this.add(this.lineObject)
    this.add(this.leftArrowObject)
    this.add(this.rightArrowObject)
    this.add(this.textObject)

    this.lineObject.userData.entity = this.entity
    this.leftArrowObject.userData.entity = this.entity
    this.rightArrowObject.userData.entity = this.entity
    this.textObject.userData.entity = this.entity
  }

  /** 根据实体属性重建线段、箭头、文字几何 */
  onUpdateGeometry() {
    const { p1, p2, offsetPoint, textPosition } = this.entity
    const dimPointData = getAlignDimensionPoints(
      new Vector3(p1.x, p1.y, 0),
      new Vector3(p2.x, p2.y, 0),
      new Vector3(offsetPoint.x, offsetPoint.y, 0)
    )

    // 更新线对象几何体
    const lines = this.getNeedRenderLines(dimPointData)
    const vertices: number[] = lines
      .flatMap((line) => [line.startPoint, line.endPoint])
      .flatMap((p) => [p.x, p.y, 0])
    const geometry = this.lineObject.geometry
    geometry.setPositions(vertices)
    geometry.attributes.position.needsUpdate = true
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    this.lineObject.computeLineDistances()

    // 更新箭头对象位置
    const { d1, d2 } = dimPointData
    const dir = new Vector3().subVectors(d2, d1).normalize()
    this.leftArrowObject.update(d1, dir, true)
    this.rightArrowObject.update(d2, dir, false)
    // 当标注长度较短时，隐藏箭头
    const isShowArrow = dimPointData.length >= 10
    this.leftArrowObject.visible = isShowArrow
    this.rightArrowObject.visible = isShowArrow

    // 更新文字对象
    this.textObject.text = this.getDimensionText(dimPointData.length)
    this.textObject.rotation.z = dimPointData.angle
    this.textObject.position.set(textPosition.x, textPosition.y, 0)
    scheduleCadTextSync(this.textObject)
  }

  /**
   * 更新标注材质及文字颜色。
   *
   * @param material - 可选的外部缓存材质；未提供时根据实体样式新建
   * @param color - 可选颜色覆盖值，未提供时沿用实体自身颜色
   */
  onUpdateMaterial(material?: LineTypeMaterial, color?: number) {
    if (!this.isCacheMaterial) {
      ;(this.lineObject.material as LineTypeMaterial).dispose()
    }
    const materialColor = color ?? this.materialColor
    this.isCacheMaterial = !!material
    this.lineObject.material = material || this.createMaterial(materialColor)
    const arrowMaterial = new MeshBasicMaterial({
      color: materialColor,
      ...pick(this.lineObject.material, ['depthTest', 'depthWrite']),
    })
    ;(this.leftArrowObject.material as MeshBasicMaterial).dispose()
    this.leftArrowObject.material = arrowMaterial
    ;(this.rightArrowObject.material as MeshBasicMaterial).dispose()
    this.rightArrowObject.material = arrowMaterial
    this.textObject.color = `#${materialColor.toString(16).padStart(6, '0')}`
    this.textObject.fontSize = this.entity.textHeight
    scheduleCadTextSync(this.textObject)
  }

  /** 释放几何体、非缓存材质与文字资源 */
  dispose() {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this.lineObject.geometry.dispose()
    if (!this.isCacheMaterial) {
      ;(this.lineObject.material as LineTypeMaterial).dispose()
    }
    this.leftArrowObject.geometry.dispose()
    ;(this.leftArrowObject.material as MeshBasicMaterial).dispose()
    this.rightArrowObject.geometry.dispose()
    ;(this.rightArrowObject.material as MeshBasicMaterial).dispose()
    disposeCadText(this.textObject)
  }

  /**
   * 克隆当前对象。
   *
   * @returns 基于同一实体新建的对齐标注对象
   */
  clone() {
    return new DimensionObject(this.entity, undefined, undefined, this.unitPresentation) as any
  }

  /**
   * 绑定实例级单位服务并刷新未覆盖的自动标注文字。
   *
   * @param service - 当前引擎实例的单位服务
   */
  setUnitPresentationService(service: UnitPresentationService): void {
    this.unitPresentation = service
    this.refreshUnitPresentation()
  }

  /** 刷新自动标注文字，不修改实体。 */
  refreshUnitPresentation(): void {
    if (this.entity.dimensionText) {
      return
    }
    const { p1, p2, offsetPoint } = this.entity
    const params = getAlignDimensionPoints(
      new Vector3(p1.x, p1.y, 0),
      new Vector3(p2.x, p2.y, 0),
      new Vector3(offsetPoint.x, offsetPoint.y, 0)
    )
    this.textObject.text = this.getDimensionText(params.length)
    scheduleCadTextSync(this.textObject)
  }

  private createLineObject(
    dimPointData: ReturnType<typeof getAlignDimensionPoints>,
    material?: LineTypeMaterial
  ) {
    const lines = this.getNeedRenderLines(dimPointData)
    const geometry = DrawUtils.getLineSegmentsGeometryByLines(lines)
    const object = createCadLineSegments2Object(geometry, material || this.createMaterial())
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()

    return object
  }

  private createMaterial(color?: number): LineTypeMaterial {
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    if (color) {
      materialOption.color = color
    }
    return MaterialUtils.createMaterialByLineType(this.entity.lineType, materialOption)
  }

  private getNeedRenderLines(params: ReturnType<typeof getAlignDimensionPoints>) {
    const { d1, d2, e1_start, e1_end, e2_start, e2_end } = params
    return [
      { startPoint: d1, endPoint: d2 },
      { startPoint: e1_start, endPoint: e1_end },
      { startPoint: e2_start, endPoint: e2_end },
    ]
  }

  private createArrowObject(params: ReturnType<typeof getAlignDimensionPoints>, color?: number) {
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    const material = new MeshBasicMaterial({
      color: color ?? this.materialColor,
      ...pick(materialOption, ['depthTest', 'depthWrite']),
    })
    // // 箭头长度取文字高度(固定2.5)
    const length = 2.5
    const leftArrow = new ArrowObject({ material, length })
    const rightArrow = new ArrowObject({ material, length })
    const { d1, d2 } = params
    const dir = new Vector3().subVectors(d2, d1).normalize()
    leftArrow.update(d1, dir, true)
    rightArrow.update(d2, dir, false)
    if (params.length < 10) {
      // 当标注长度较短时，隐藏箭头
      leftArrow.visible = false
      rightArrow.visible = false
    }
    return { leftArrow, rightArrow }
  }

  private createTextObject(
    params: ReturnType<typeof getAlignDimensionPoints>,
    materialColor?: number
  ) {
    const { textPosition, dimensionText, textHeight } = this.entity
    const color = materialColor ?? this.materialColor
    const object = createCadText()
    object.text = dimensionText || this.getDimensionText(params.length)
    object.fontSize = textHeight
    object.color = `#${color.toString(16).padStart(6, '0')}`
    object.position.set(textPosition.x, textPosition.y, 0)
    object.anchorX = 'center'
    object.anchorY = 'bottom'
    object.rotation.z = params.angle
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    object.material.depthTest = materialOption.depthTest ?? true
    object.material.depthWrite = materialOption.depthWrite ?? true
    scheduleCadTextSync(object)
    return object
  }

  private getDimensionText(length: number): string {
    if (this.entity.dimensionText) {
      return this.entity.dimensionText
    }
    return formatLength(length, this.unitPresentation?.preference ?? DEFAULT_UNIT_PREFERENCE)
      .valueText
  }
}
